import type { AttributeOffsets } from "./attributeOffsets";
import { effectiveConfig, type PredictorConfig } from "./config";
import { compareDateTime, dateOf, type IsoDate } from "./dates";
import type { Bean, Grinder, Shot } from "./model";
import { classify, fit } from "./predictor";
import { beanSlope, combineSlopes, withLearnedSlope, type BeanSlope } from "./slopeLearning";
import { asPseudoObservation, beanSummary, prior, reference, type BeanSummary } from "./startingPoint";

/**
 * Leave-the-future-out replay of a phone's history. Mirrors Replay.java.
 *
 * Shots are walked in time order. Each valid shot is predicted from only what came before it: the same bean's
 * earlier shots on that grinder, a starting point from the other beans dialed in by then, and the grinder slope
 * learned from the beans so far. The error is in ln-time at the grind actually pulled:
 *
 *   e = ln(t_norm) − (ln a + b · x)
 *
 * Choked and channeled shots are not scored but still join the history (they act as bounds later). Attribute
 * offsets are held at the values passed in (a small look-ahead, accepted to keep the replay cheap).
 *
 *   score = mean Huber_δ(e) + (mean e)²     δ = 0.10;  the second term penalises bias.
 */
export interface ReplayInput {
  /** grinders with their seed slopes */
  readonly grinders: readonly Grinder[];
  /** every bean that has shots (order fixes the summation order of x_ref) */
  readonly beans: readonly Bean[];
  /** active shots only (the app leaves out shots before a recalibration) */
  readonly shots: readonly Shot[];
  readonly offsets: AttributeOffsets;
}

export interface ReplayScore {
  /** errors in time order */
  readonly errors: readonly number[];
  readonly score: number;
  /** the same score over the last RECENT_SHOTS errors */
  readonly recentScore: number;
}

export const HUBER_DELTA = 0.10;
export const RECENT_SHOTS = 20;

export function huber(e: number, delta = HUBER_DELTA): number {
  const a = Math.abs(e);
  return a <= delta ? 0.5 * e * e : delta * (a - 0.5 * delta);
}

/** mean Huber + mean² ; NaN for no errors. */
export function scoreErrors(errors: readonly number[]): number {
  const n = errors.length;
  if (n === 0) return NaN;
  let h = 0, m = 0;
  for (const e of errors) { h += huber(e); m += e; }
  m /= n;
  return h / n + m * m;
}

interface BeanTrack {
  readonly bean: Bean;
  readonly shots: Shot[];
  slope: BeanSlope | null;
  /** cached summary and the slope it was computed with */
  summary: BeanSummary | null;
  summarySlope: number;
  summaryCount: number;
}

/** Shots in replay order: timestamp, then id (nulls last), then input order. */
export function replayOrder(shots: readonly Shot[]): Shot[] {
  return shots
    .map((s, i) => ({ s, i }))
    .sort((a, b) => {
      const c = compareDateTime(a.s.timestamp, b.s.timestamp);
      if (c !== 0) return c;
      const ia = a.s.id ?? Number.MAX_SAFE_INTEGER, ib = b.s.id ?? Number.MAX_SAFE_INTEGER;
      return ia !== ib ? ia - ib : a.i - b.i;
    })
    .map((x) => x.s);
}

export function replay(input: ReplayInput, cfg: PredictorConfig): ReplayScore {
  const grinders = new Map<number, Grinder>();
  for (const g of input.grinders) if (g.id !== null) grinders.set(g.id, g);
  const beanIndex = new Map<number, number>();
  input.beans.forEach((b, i) => { if (b.id !== null) beanIndex.set(b.id, i); });

  // per grinder: tracks in input-bean order
  const tracks = new Map<number, (BeanTrack | undefined)[]>();
  const errors: number[] = [];

  for (const s of replayOrder(input.shots)) {
    const g = grinders.get(s.grinderId);
    const bi = beanIndex.get(s.beanId);
    if (g === undefined || bi === undefined) continue;
    const bean = input.beans[bi] as Bean;
    let list = tracks.get(s.grinderId);
    if (list === undefined) { list = new Array(input.beans.length); tracks.set(s.grinderId, list); }
    let track = list[bi];
    if (track === undefined) {
      track = { bean, shots: [], slope: null, summary: null, summarySlope: NaN, summaryCount: -1 };
      list[bi] = track;
    }

    const cb = effectiveConfig(cfg, bean);
    const tNorm = (s.timeSeconds * cb.targetYieldG) / s.yieldG;
    if (classify(tNorm, cb) === "valid") {
      const day: IsoDate = dateOf(s.timestamp);
      const slopes: BeanSlope[] = [];
      for (const t of list) if (t !== undefined && t.slope !== null) slopes.push(t.slope);
      const gE = withLearnedSlope(g, combineSlopes(g, slopes, day, cfg));

      const summaries: BeanSummary[] = [];
      for (const t of list) {
        if (t === undefined || t === track || t.shots.length === 0) continue;
        if (t.summaryCount !== t.shots.length || t.summarySlope !== gE.priorSlopeB) {
          const last = dateOf((t.shots[t.shots.length - 1] as Shot).timestamp);
          const f = fit(gE, t.bean, t.shots, last, cfg);
          t.summary = f.nEff >= cfg.xDialedMinNeff
            ? beanSummary(t.bean, gE, f.prediction.grindSettingRaw, last, input.offsets, cfg) : null;
          t.summarySlope = gE.priorSlopeB;
          t.summaryCount = t.shots.length;
        }
        if (t.summary !== null) summaries.push(t.summary);
      }
      const ref = reference(gE, summaries, day, cfg);
      const pr = prior(gE, bean, ref, input.offsets, day, cb);
      const f = fit(gE, bean, track.shots, day, cfg, asPseudoObservation(pr, cb), "");
      const p = f.prediction;
      if (!Number.isNaN(p.lnIntercept)) errors.push(Math.log(tNorm) - (p.lnIntercept + p.slopeUsed * s.grindSetting));
    }

    track.shots.push(s);
    track.slope = beanSlope(g, bean, track.shots, cfg);
  }

  return { errors, score: scoreErrors(errors), recentScore: scoreErrors(errors.slice(-RECENT_SHOTS)) };
}
